<script setup lang="ts">
/**
 * Light / System / Dark. `full`: a three-way segmented control. `compact`: one button that cycles (collapsed
 * sidebar, sign-in pages). `tone="sidebar"` styles it for the always-dark sidebar.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { THEME_OPTIONS, themeChoice, type ThemeChoice } from '../utils/theme';

const { t } = useI18n();
const props = withDefaults(defineProps<{ variant?: 'full' | 'compact'; tone?: 'sidebar' | 'page' }>(), { variant: 'full', tone: 'page' });

const current = computed(() => THEME_OPTIONS.find((o) => o.value === themeChoice.value)!);
const ORDER: ThemeChoice[] = ['light', 'dark', 'system'];
const cycle = () => (themeChoice.value = ORDER[(ORDER.indexOf(themeChoice.value) + 1) % ORDER.length]!);
const sidebar = computed(() => props.tone === 'sidebar');
</script>

<template>
  <div
    v-if="variant === 'full'"
    class="flex rounded-md p-0.5"
    :class="sidebar ? 'bg-white/5' : 'border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900'"
    role="radiogroup"
    :aria-label="t('theme.label')"
  >
    <button
      v-for="o in THEME_OPTIONS"
      :key="o.value"
      type="button"
      role="radio"
      :aria-checked="themeChoice === o.value"
      :title="t('theme.titleOption', { name: t(`theme.${o.value}`) })"
      class="flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs transition-colors"
      :class="
        themeChoice === o.value
          ? sidebar
            ? 'bg-white/15 text-white'
            : 'bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-white'
          : sidebar
            ? 'text-slate-400 hover:text-white'
            : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
      "
      @click="themeChoice = o.value"
    >
      <svg viewBox="0 0 20 20" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="o.icon" /></svg>
      {{ t(`theme.${o.value}`) }}
    </button>
  </div>
  <button
    v-else
    type="button"
    class="rounded-md p-1.5"
    :class="sidebar ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white'"
    :aria-label="t('theme.current', { name: t(`theme.${current.value}`) })"
    :title="t('theme.currentTitle', { name: t(`theme.${current.value}`) })"
    @click="cycle"
  >
    <svg viewBox="0 0 20 20" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="current.icon" /></svg>
  </button>
</template>
